/**
 * Expense list — `GET expenses/`, scoped to the caller by the backend
 * (landlord: owned properties; agent: `property.assigned_agent = self`, with
 * NO owner fallback — see the "Agent scoping is inconsistent" entry in
 * refactor/docs/PARITY_NOTES.md; superagent: all). Property/category filters
 * are server-side. Manager roles only — the route itself is gated (this page
 * is only ever mounted under the landlord/agent trees), matching the standard
 * `assertManagerRole` write gate this module uses everywhere else.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Receipt, Trash2 } from 'lucide-react';
import { parseApiError } from '../../lib/apiError';
import { useIsMobile } from '../../lib/useIsMobile';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { deleteExpense, listExpenses } from '../../api/endpoints/expenses';
import { EXPENSE_CATEGORIES } from '../../types/expense';
import type { ExpenseCategory } from '../../types/expense';

export function ExpenseListPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [page, setPage] = useState(1);
  const [category, setCategory] = useState<ExpenseCategory | ''>('');
  const [property, setProperty] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const query = useQuery({
    queryKey: ['expenses', { page, category, property }],
    queryFn: () =>
      listExpenses({
        page,
        category: category === '' ? undefined : category,
        property: property === '' ? undefined : Number(property),
      }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteExpense(id),
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
    onError: (caught) => setActionError(parseApiError(caught).message),
  });

  const expenses = query.data?.results ?? [];
  const hasNext = query.data?.next !== null && query.data?.next !== undefined;
  const hasPrevious = query.data?.previous !== null && query.data?.previous !== undefined;
  const pendingExpense = expenses.find((expense) => expense.id === confirmDeleteId) ?? null;

  return (
    <section>
      <h2>Expenses</h2>

      <p>
        <Link to="new">Log expense</Link> <Link to="summary">Summary</Link>
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
        }}
      >
        <label htmlFor="expense-property">Property id</label>
        <input
          id="expense-property"
          type="number"
          value={property}
          onChange={(event) => setProperty(event.target.value)}
        />

        <label htmlFor="expense-category">Category</label>
        <select
          id="expense-category"
          value={category}
          onChange={(event) => {
            setCategory(event.target.value as ExpenseCategory | '');
            setPage(1);
          }}
        >
          <option value="">All</option>
          {EXPENSE_CATEGORIES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        <button type="submit">Apply</button>
      </form>

      {actionError !== null && (
        <p role="alert" className="form__error">
          {actionError}
        </p>
      )}

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}

      {query.isSuccess && expenses.length === 0 && (
        <EmptyState icon={Receipt} message="No expenses found." action={{ label: 'Log expense', to: 'new' }} />
      )}

      {query.isSuccess && expenses.length > 0 && (
        isMobile ? (
          <ul className="record-cards">
            {expenses.map((expense) => (
              <li key={expense.id} className="record-card">
                <div className="record-card__row">
                  <span className="record-card__label">Property</span>
                  <span className="record-card__value">{expense.property_name}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Category</span>
                  <span className="record-card__value">{expense.category}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Amount</span>
                  <span className="record-card__value">{expense.amount}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Date</span>
                  <span className="record-card__value">{expense.date}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Vendor</span>
                  <span className="record-card__value">{expense.vendor === '' ? '—' : expense.vendor}</span>
                </div>
                <div className="record-card__actions">
                  <Link to={`${expense.id}/edit`} className="button button--outlined">
                    <Pencil size={14} className="icon-inline" aria-hidden="true" />
                    Edit
                  </Link>
                  <button
                    type="button"
                    className="button--danger"
                    disabled={remove.isPending}
                    onClick={() => setConfirmDeleteId(expense.id)}
                  >
                    <Trash2 size={14} className="icon-inline" aria-hidden="true" />
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Property</th>
                <th>Category</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Vendor</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense) => (
                <tr key={expense.id}>
                  <td>{expense.property_name}</td>
                  <td>{expense.category}</td>
                  <td>{expense.amount}</td>
                  <td>{expense.date}</td>
                  <td>{expense.vendor === '' ? '—' : expense.vendor}</td>
                  <td>{expense.description}</td>
                  <td>
                    <span className="row-actions">
                      <Link to={`${expense.id}/edit`}>
                        <Pencil size={14} className="icon-inline" aria-hidden="true" />
                        Edit
                      </Link>
                      <button
                        type="button"
                        className="button--danger"
                        disabled={remove.isPending}
                        onClick={() => setConfirmDeleteId(expense.id)}
                      >
                        <Trash2 size={14} className="icon-inline" aria-hidden="true" />
                        Delete
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      <ConfirmDialog
        open={pendingExpense !== null}
        title="Delete expense"
        message="Delete this expense? This cannot be undone."
        tone="danger"
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingExpense !== null) remove.mutate(pendingExpense.id);
          setConfirmDeleteId(null);
        }}
        onCancel={() => setConfirmDeleteId(null)}
      />

      <p>
        <button type="button" disabled={!hasPrevious} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>{' '}
        <span>Page {page}</span>{' '}
        <button type="button" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </p>
    </section>
  );
}

export default ExpenseListPage;
